/**
 * Theme AST slice. After the editor loads the user's app files, the on-open
 * AST sync (ast/pipeline/runFilesAstSync) parses `themeConstants.tsx` and
 * dispatches the extracted tokens here. ThemePanel writes to BOTH the file
 * (via the codec) and this slice on every edit so any subscriber sees the
 * current theme without re-parsing.
 */
import isEqual from 'lodash/isEqual';

import type { ThemeData } from '@/ast/readers/theme/parse';
import type { RootState } from '@/store/rootState';

export interface ThemeAstState {
  data: ThemeData | null;
  /** Source contents the data was last derived from. Lets the reducer
   *  cheaply skip re-dispatch when nothing changed. */
  source: string | null;
}

const themeAstInitial: ThemeAstState = { data: null, source: null };

export type ThemeAstAction =
  | { type: 'THEME_AST_SET'; data: ThemeData; source: string }
  | { type: 'THEME_AST_CLEAR' };

export function themeAst(state: ThemeAstState = themeAstInitial, action: any): ThemeAstState {
  switch ((action as ThemeAstAction).type) {
    case 'THEME_AST_SET': {
      const a = action as Extract<ThemeAstAction, { type: 'THEME_AST_SET' }>;
      // Skip redundant dispatches — keeps subscribers stable.
      if (state.source === a.source && isEqual(state.data, a.data)) return state;
      return { data: a.data, source: a.source };
    }
    case 'THEME_AST_CLEAR':
      return themeAstInitial;
    default:
      return state;
  }
}

export const setThemeAst = (data: ThemeData, source: string): ThemeAstAction => ({
  type: 'THEME_AST_SET',
  data,
  source,
});

export const selectThemeAst = (state: RootState) => state.themeAst.data;
